import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Center,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { ArrowLeft, Search } from 'lucide-react';
import { MESSAGE_LIMITS } from '@twinden/shared';
import { searchConversation } from '../../lib/searchIndex';
import { api, ApiError } from '../../lib/api';
import { appLocale } from '../../lib/format';
import { t as tNow, useT } from '../../i18n';
import { TwindenLoader } from '../brand/TwindenLoader';
import { useLoadingState } from '../../hooks/useLoadingState';

export interface SearchHit {
  id: string;
  content: string;
  createdAt: string;
  sender: { id: string; displayName: string } | null;
}

interface ConversationSearchProps {
  conversationId: string;
  onClose: () => void;
  onSelect: (hit: SearchHit) => void;
}

const DEBOUNCE_MS = 300;

// The matched text with the hit in bold, trimmed around the first match so
// it's visible in one or two lines.
function Snippet({ text, query }: { text: string; query: string }) {
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index < 0) return <>{text}</>;
  const start = Math.max(0, index - 40);
  return (
    <>
      {start > 0 && '…'}
      {text.slice(start, index)}
      <Text
        as="mark"
        bg="yellow.200"
        color="gray.900"
        borderRadius="sm"
        px="1px"
      >
        {text.slice(index, index + query.length)}
      </Text>
      {text.slice(index + query.length)}
    </>
  );
}

// Search within the open chat (M20, D26).
export function ConversationSearch({
  conversationId,
  onClose,
  onSelect,
}: ConversationSearchProps) {
  const [query, setQuery] = useState('');
  const t = useT();
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [loading, setLoading] = useState(false);
  // The small mark for results, after 150 ms (M66, D250).
  const resultsWait = useLoadingState({ loading });
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const bg = useColorModeValue('white', 'gray.800');
  const trimmed = query.trim();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (trimmed.length < MESSAGE_LIMITS.SEARCH_MIN_LENGTH) {
      setHits(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      // Encrypted messages can only be found on this device (M28, D61);
      // the server still finds older plain ones the index hasn't reached.
      Promise.all([
        searchConversation(conversationId, trimmed),
        api.searchMessages(conversationId, trimmed).then(
          (response) => response.data as SearchHit[],
          () => [] as SearchHit[]
        ),
      ])
        .then(([local, server]) => {
          if (cancelled) return;
          const merged = new Map<string, SearchHit>();
          for (const hit of local) {
            merged.set(hit.id, {
              id: hit.id,
              content: hit.text,
              createdAt: hit.createdAt,
              sender: hit.senderId
                ? { id: hit.senderId, displayName: hit.senderName }
                : null,
            });
          }
          for (const hit of server) {
            if (!merged.has(hit.id) && hit.content) merged.set(hit.id, hit);
          }
          setHits(
            [...merged.values()].sort((a, b) =>
              b.createdAt.localeCompare(a.createdAt)
            )
          );
          setError(null);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(
            err instanceof ApiError ? err.message : tNow('chat.search.failed')
          );
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [conversationId, trimmed]);

  return (
    <Box
      position="absolute"
      inset={0}
      zIndex={2}
      bg={bg}
      display="flex"
      flexDirection="column"
    >
      <HStack
        px={{ base: 1, md: 4 }}
        pt={{ base: 'max(env(safe-area-inset-top), 0.5rem)', md: 3 }}
        pb={{ base: 2, md: 3 }}
        borderBottomWidth="1px"
        borderColor={borderColor}
        spacing={1}
      >
        <IconButton
          aria-label={t('chat.search.close')}
          icon={<ArrowLeft size={20} />}
          variant="ghost"
          onClick={onClose}
        />
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <Search size={18} />
          </InputLeftElement>
          <Input
            ref={inputRef}
            placeholder={t('chat.header.search')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') onClose();
            }}
            enterKeyHint="search"
          />
        </InputGroup>
      </HStack>

      <VStack
        flex="1"
        overflowY="auto"
        align="stretch"
        spacing={0}
        sx={{ overscrollBehaviorY: 'contain' }}
      >
        {loading && (
          <Center py={6}>
            {resultsWait.loader && <TwindenLoader size="inline" />}
          </Center>
        )}
        {!loading && error && (
          <Text p={6} color="red.500" textAlign="center">
            {error}
          </Text>
        )}
        {!loading && !error && hits?.length === 0 && (
          <Text p={6} color="gray.500" textAlign="center">
            {t('chat.search.noMatch', { query: trimmed })}
          </Text>
        )}
        {!loading && !error && !hits && (
          <Text p={6} color="gray.500" textAlign="center" fontSize="sm">
            {t('chat.search.typeAtLeast', {
              n: MESSAGE_LIMITS.SEARCH_MIN_LENGTH,
            })}
          </Text>
        )}
        {!loading &&
          hits?.map((hit) => (
            <Box
              key={hit.id}
              as="button"
              type="button"
              textAlign="left"
              px={4}
              py={3}
              borderBottomWidth="1px"
              borderColor={borderColor}
              _hover={{ bg: hoverBg }}
              onClick={() => onSelect(hit)}
            >
              <HStack justify="space-between" spacing={2}>
                <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                  {hit.sender?.displayName ?? t('chat.unknown')}
                </Text>
                <Text fontSize="xs" color="gray.500" flexShrink={0}>
                  {new Date(hit.createdAt).toLocaleDateString(appLocale())}
                </Text>
              </HStack>
              <Text fontSize="sm" noOfLines={2}>
                <Snippet text={hit.content} query={trimmed} />
              </Text>
            </Box>
          ))}
      </VStack>
    </Box>
  );
}
